import React, { useState, useEffect } from 'react';
import { Fetcher } from '@this/src/util';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import { Select } from '@this/shared/ui/inputs/select';
import { Text } from '@this/shared/ui/data_displays/typography/text';
import { Heading, HEADING_LEVEL } from '@this/shared/ui/data_displays/typography/heading';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Content, Title } from '../god';

interface UserWithTokens {
  id: number;
  email: string;
  token_count: number;
  tokens: Array<{
    platform: string;
    token: string;
  }>;
}

interface NotificationResult {
  success: boolean;
  success_count?: number;
  failure_count?: number;
  error?: string;
  response?: any;
}

const FcmTest: React.FC = () => {
  const [usersWithTokens, setUsersWithTokens] = useState<UserWithTokens[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [targetType, setTargetType] = useState<'user' | 'token'>('user');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [token, setToken] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<NotificationResult | null>(null);
  const [selectedUserTokens, setSelectedUserTokens] = useState<UserWithTokens['tokens']>([]);

  useEffect(() => {
    fetchUsersWithTokens();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      const user = usersWithTokens.find(u => u.id.toString() === selectedUserId);
      setSelectedUserTokens(user?.tokens || []);
    } else {
      setSelectedUserTokens([]);
    }
  }, [selectedUserId, usersWithTokens]);

  const fetchUsersWithTokens = async () => {
    try {
      setLoading(true);
      const response = await Fetcher.get<UserWithTokens[]>('/god/fcm_test.json');
      setUsersWithTokens(response);
    } catch (error) {
      console.error('Failed to fetch users with tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      setSending(true);
      setResult(null);

      const params = {
        target_type: targetType,
        ...(targetType === 'user' ? { user_id: selectedUserId } : { token }),
        title: title || undefined,
        body: body || undefined
      };

      const response = await Fetcher.post<NotificationResult>('/god/fcm_test/send_notification', params);
      setResult(response);
    } catch (error: any) {
      setResult({
        success: false,
        error: error.message || '通知の送信に失敗しました'
      });
    } finally {
      setSending(false);
    }
  };

  const isFormValid = () => {
    if (targetType === 'user') {
      return selectedUserId !== '';
    }
    return token.trim() !== '';
  };

  if (loading) {
    return (
      <Content>
        <Title>FCMプッシュ通知テスト（God管理画面）</Title>
        <Loading />
      </Content>
    );
  }

  return (
    <Content>
      <Title>FCMプッシュ通知テスト（God管理画面）</Title>

      <div style={{ display: 'flex', gap: '40px' }}>
        <div style={{ flex: 2 }}>
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '20px',
              marginBottom: '20px'
            }}
          >
            <div style={{ marginTop: 0, marginBottom: '20px' }}>
              <Heading level={HEADING_LEVEL['3']}>通知送信フォーム</Heading>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <Text style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>送信対象</Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="targetType"
                    value="user"
                    checked={targetType === 'user'}
                    onChange={e => setTargetType(e.target.value as 'user')}
                  />
                  ユーザーを選択
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    name="targetType"
                    value="token"
                    checked={targetType === 'token'}
                    onChange={e => setTargetType(e.target.value as 'token')}
                  />
                  トークンを直接入力
                </label>
              </div>
            </div>

            {targetType === 'user' && (
              <div style={{ marginBottom: '20px' }}>
                <Text style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>ユーザー選択</Text>
                <Select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                  <option value="">-- ユーザーを選択 --</option>
                  {usersWithTokens.map(user => (
                    <option key={user.id} value={user.id.toString()}>
                      {user.email} (トークン数: {user.token_count})
                    </option>
                  ))}
                </Select>

                {selectedUserTokens.length > 0 && (
                  <div
                    style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px'
                    }}
                  >
                    <Text style={{ fontWeight: 'bold' }}>登録されているトークン:</Text>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {selectedUserTokens.map((tokenInfo, index) => (
                        <li key={index}>
                          {tokenInfo.platform}: {tokenInfo.token}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {targetType === 'token' && (
              <div style={{ marginBottom: '20px' }}>
                <Text style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>FCMトークン</Text>
                <Input value={token} onChange={e => setToken(e.target.value)} placeholder="FCMトークンを入力" />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <Text style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                タイトル（省略可）
              </Text>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="デフォルト: AI Travel テスト通知"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <Text style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>本文（省略可）</Text>
              <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="デフォルト: God管理画面からのテスト通知 - [送信時刻]"
                rows={3}
              />
            </div>

            <Button onClick={handleSendNotification} disabled={!isFormValid() || sending} loading={sending}>
              通知を送信
            </Button>

            {result && (
              <div
                style={{
                  marginTop: '20px',
                  padding: '15px',
                  borderRadius: '4px',
                  backgroundColor: result.success ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`,
                  color: result.success ? '#155724' : '#721c24'
                }}
              >
                <Text style={{ fontWeight: 'bold' }}>{result.success ? '成功!' : 'エラー!'}</Text>
                {result.success ? (
                  <div>
                    <Text>通知が送信されました。</Text>
                    <Text>成功数: {result.success_count}</Text>
                    <Text>失敗数: {result.failure_count}</Text>
                  </div>
                ) : (
                  <Text>{result.error}</Text>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              border: '1px solid #d1ecf1',
              borderRadius: '4px',
              padding: '20px',
              backgroundColor: '#d1ecf1'
            }}
          >
            <div style={{ marginTop: 0, marginBottom: '15px' }}>
              <Heading level={HEADING_LEVEL['3']}>使い方</Heading>
            </div>
            <ol style={{ paddingLeft: '20px' }}>
              <li>送信対象を選択（ユーザーまたはトークン直接入力）</li>
              <li>ユーザーを選択した場合は、ドロップダウンから選択</li>
              <li>トークンを直接入力する場合は、FCMトークンを入力</li>
              <li>必要に応じてタイトルと本文を入力（省略可）</li>
              <li>「通知を送信」ボタンをクリック</li>
            </ol>

            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <Heading level={HEADING_LEVEL['3']}>注意事項</Heading>
            </div>
            <ul style={{ paddingLeft: '20px' }}>
              <li>テスト環境では実際の通知は送信されません</li>
              <li>FCMサーバーキーが設定されていることを確認してください</li>
              <li>無効なトークンは自動的に削除されます</li>
            </ul>
          </div>
        </div>
      </div>
    </Content>
  );
};

export default FcmTest;
